import { mapHelper } from "../../utils/maps/map-helper";
import { MapMarkerType, MarkerConfig } from "../../utils/maps/map-types";
import { PrimaryLotType } from "./LotMap";
import { MAP_STYLE } from "./LotMapStyle";

export const enum LotStatus {
	Unavailable = "unavailable",
	Available = "available",
	QuickMove = "quickmove",
	Sold = "sold",
}

export type LotProps = {
	id: string;
	number: string;
	sfid: string;
	parcel: number;
};

const DEBUG_VERBOSE: boolean = false;
export class Lot {
	readonly map: google.maps.Map | null = null;
	readonly layer!: google.maps.Data;
	readonly feature: google.maps.Data.Feature;

	private data: any;
	private label: any;
	private marker: any;
	public parcelId: any;

	constructor(
		feature: google.maps.Data.Feature,
		target: google.maps.Map | google.maps.Data | null = null
	) {
		this.feature = feature;
		this.data = feature.getProperty("lot") as any;
		this.parcelId = this.data["parcelId"] as any;

		if (target instanceof google.maps.Map) {
			this.map = target;
			this.layer = this.map.data;
		} else if (target instanceof google.maps.Data) {
			this.layer = target;
			this.map = this.layer.getMap();
		}

		this.layer.overrideStyle(feature, this.defaultStyle);

		this.addLotNumber(this.data.number);

		DEBUG_VERBOSE && console.log("ADDED LOT: ", this.data);
		DEBUG_VERBOSE && console.log(" - Status: " + this.status);
		DEBUG_VERBOSE && console.log(" - Available: " + this.isAvailable);
		DEBUG_VERBOSE && console.log(" - QuickMove: " + this.isQuickMove);
		DEBUG_VERBOSE && console.log(" - Model: " + this.isModel);
		DEBUG_VERBOSE && console.log(" - Center: " + this.center);
		DEBUG_VERBOSE && console.log(" - Bounds: " + this.bounds);
		DEBUG_VERBOSE && console.log(" - Parcel ID: " + this.parcelId);
	}

	get defaultStyle(): google.maps.FeatureStyleOptions {
		const primaryLotType = this.type as keyof typeof MAP_STYLE;

		DEBUG_VERBOSE && console.log("Primary Type: ", primaryLotType);

		const style: google.maps.FeatureStyleOptions = {
			strokeOpacity: 1,
			strokeColor: "#F5F5F5",
			strokeWeight: 2.5,
			fillOpacity: 1,
			...MAP_STYLE[primaryLotType],
		};

		return style;
	}

	addLotNumber(number: string) {
		if (!this.map) {
			console.warn(
				"The current layer doesn't have a _map property, which is needed to add lot numbers."
			);
			return;
		}

		const markerConfig: MarkerConfig = {
			type: MapMarkerType.Text,
			feature: this.feature,
			label: number,
			map: this.map,
			inactive: !this.isInteractive,
		};
		this.label = mapHelper.addMarker(markerConfig);
	}

	addMarker() {
		if (this.map) {
			// Remove the numeric label
			this.label?.setMap(null);

			const markerConfig: MarkerConfig = {
				type: MapMarkerType.Badge,
				feature: this.feature,
				label: "LOT " + this.number,
				map: this.map,
			};
			this.marker = mapHelper.addMarker(markerConfig);
			DEBUG_VERBOSE && console.log("Added marker", this.marker);
		}
	}

	get type(): string {
		if (this.isModel) {
			return PrimaryLotType.model;
		} else if (this.isUnavailable) {
			return PrimaryLotType.unavailable;
		} else if (this.isSold) {
			return PrimaryLotType.sold;
		} else if (this.isQuickMove) {
			return PrimaryLotType.quickmove;
		} else if (this.isAvailable) {
			return PrimaryLotType.available;
		} else {
			return PrimaryLotType.inactive;
		}
	}

	onHover() {
		if (!this.isInteractive) {
			return;
		}

		this.layer?.overrideStyle(this.feature, {
			// fillColor: "#00D0FA",
			fillOpacity: 0.7,
		});

		this.label?.element?.classList.add("hhds-map-textmarker__hover");
	}

	onLeave() {
		if (!this.isInteractive) return;
		this.layer?.overrideStyle(this.feature, this.defaultStyle);
		this.label?.element?.classList.remove("hhds-map-textmarker__hover");
	}

	get geometry(): any {
		return this.feature.getGeometry();
	}

	get number(): string {
		return this.data.number;
	}

	get id(): string {
		return this.data.id;
	}

	get isFocused(): boolean {
		return this.marker !== null;
	}

	get isInteractive(): boolean {
		return this.isModel || this.status == LotStatus.Available;
	}

	get status(): LotStatus {
		return this.data.status as LotStatus;
	}

	get isAvailable(): boolean {
		return this.status == LotStatus.Available;
	}

	get isUnavailable(): boolean {
		return this.status == LotStatus.Unavailable;
	}

	get isModel(): boolean {
		return this.data.model;
	}

	get isSold(): boolean {
		return this.status == LotStatus.Sold;
	}

	get isQuickMove(): boolean {
		return this.data.quickmove;
	}

	get center(): google.maps.LatLng | null | undefined {
		return this.getPosition();
	}

	get bounds(): google.maps.LatLngBounds | null | undefined {
		const bounds = new google.maps.LatLngBounds();

		if (this.geometry?.getType() === "MultiPolygon") {
			this.geometry.getArray().forEach((polygon: any) => {
				const ring = polygon?.getArray()[0]?.getArray();
				ring.forEach((latlng: any) => bounds.extend(latlng));
			});
		} else if (this.geometry?.getType() === "Polygon") {
			this.geometry
				.getArray()[0]
				.getArray()
				.forEach((latlng: any) => bounds.extend(latlng));
		} else if (this.geometry?.getType() === "Point") {
			bounds.extend(this.geometry.get());
		}

		return bounds;
	}

	delete() {
		if (this.label) {
			this.label.setMap(null);
			this.label = null;
		}
		if (this.layer) {
			this.layer.remove(this.feature);
		}
	}

	private getPosition(): google.maps.LatLng | null | undefined {
		let position: google.maps.LatLng | null = null;

		const geometry = this.geometry;
		if (geometry?.getType() === "MultiPolygon") {
			let bounds = new google.maps.LatLngBounds();
			geometry.getArray().forEach((polygon: any) => {
				const ring = polygon?.getArray()[0]?.getArray(); // outer ring
				ring.forEach((latlng: any) => bounds!.extend(latlng));
			});
			position = bounds!.getCenter();
		} else if (geometry?.getType() === "Polygon") {
			const bounds = new google.maps.LatLngBounds();
			geometry
				.getArray()[0]
				.getArray()
				.forEach((latlng: any) => bounds.extend(latlng));
			position = bounds.getCenter();
		} else if (geometry?.getType() === "Point") {
			position = geometry.get();
		}

		return position;
	}
}
